import { ColumnDef } from "@tanstack/react-table"
import { sortableColumnHeader } from "@/components/data-table"
import { Badge } from "@/components/tailwind/badge"
import { Button } from "@/components/tailwind/button"
import { Text } from "@/components/tailwind/text"
import { LedgerEntry } from "@/models/ledger"
import { formatDateString } from "@/lib/utils"
import { Info, Pencil, Printer, Trash2 } from "lucide-react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useLedgerTranslation } from "@/i18n/hooks"

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

function formatTimestamp(timestamp: string | null | undefined): string | null {
  if (!timestamp) return null
  const date = new Date(timestamp)
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function TooltipContentComponent({entry}: {entry: LedgerEntry}) {
  const { t } = useLedgerTranslation()
  
  return (
    <div className="space-y-2 text-xs">
      {entry.createdAt && (
        <div className="flex justify-between items-start gap-2">
          <span className="text-zinc-500 dark:text-zinc-400 shrink-0">
            {t('metadata.created')}:
          </span>
          <div className="text-right">
            <div className="text-zinc-900 dark:text-zinc-100">
              {formatTimestamp(entry.createdAt)}
            </div>
            {entry.createdBy && (
              <div className="text-zinc-600 dark:text-zinc-400 mt-0.5">
                {entry.createdBy}
              </div>
            )}
          </div>
        </div>
      )}
      {entry.updatedAt && (
        <div className="flex justify-between items-start gap-2">
          <span className="text-zinc-500 dark:text-zinc-400 shrink-0">
            {t('metadata.updated')}:
          </span>
          <div className="text-right">
            <div className="text-zinc-900 dark:text-zinc-100">
              {formatTimestamp(entry.updatedAt)}
            </div>
            {entry.updatedBy && (
              <div className="text-zinc-600 dark:text-zinc-400 mt-0.5">
                {entry.updatedBy}
              </div>
            )}
          </div>
        </div>
      )}
      {entry.cancelledAt && (
        <div className="flex justify-between items-start gap-2 pt-2 mt-2 border-t border-zinc-200 dark:border-zinc-700">
          <span className="text-red-600 dark:text-red-400 font-medium shrink-0">
            {t('metadata.cancelled')}:
          </span>
          <div className="text-right">
            <div className="text-zinc-900 dark:text-zinc-100">
              {formatTimestamp(entry.cancelledAt)}
            </div>
            {entry.cancelledBy && (
              <div className="text-zinc-600 dark:text-zinc-400 mt-0.5">
                {entry.cancelledBy}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function DescriptionCell({entry}: {entry: LedgerEntry}) {
  const { t } = useLedgerTranslation()
  return (
    <Text className={entry.isCancelled ? 'line-through decoration-2' : ''}>
      {entry.entryPurpose === 'ORDER_DONATION'
        ? t('automatic.orderDonation', { orderNo: entry.orderNumber })
        : entry.orderId && !entry.description
          ? t('automatic.orderFee', { orderNo: entry.orderNumber })
          : entry.description}
    </Text>
  )
}

function TypeHeader() {
  const { t } = useLedgerTranslation()
  return <>{t('fields.type')}</>
}

function TypeCell({entry}: {entry: LedgerEntry}) {
  const { t } = useLedgerTranslation()
  
  if (entry.type === 'OPENING_BALANCE') {
    return (
      <Badge
        color="blue"
        className={entry.isCancelled ? 'opacity-50 line-through' : ''}
      >
        {t('types.openingBalance')}
      </Badge>
    )
  }
  
  return (
    <Badge
      color={entry.type === 'INCOME' ? 'green' : 'red'}
      className={entry.isCancelled ? 'opacity-50 line-through' : ''}
    >
      {entry.type === 'INCOME' ? t('types.income') : t('types.expense')}
    </Badge>
  )
}

function ActionsHeader() {
  const { t } = useLedgerTranslation()
  return <>{t('fields.actions')}</>
}

interface ActionsCellProps {
  entry: LedgerEntry
  onEditEntry?: (entry: LedgerEntry) => void
  onCancelEntry?: (entry: LedgerEntry) => void
  onPrintReceipt?: (entry: LedgerEntry) => void
}

function ActionsCell({entry, onEditEntry, onCancelEntry, onPrintReceipt}: ActionsCellProps) {
  const { t } = useLedgerTranslation()

  const isEditDisabled = !onEditEntry || entry.isCancelled || entry.orderId !== null || entry.type === 'OPENING_BALANCE'
  const isCancelDisabled = !onCancelEntry || entry.isCancelled || entry.orderId !== null || entry.type === 'OPENING_BALANCE'
  // Loose null-check on purpose: orderId may arrive as null or be omitted (undefined) for manual entries.
  const isPrintDisabled = !onPrintReceipt || entry.isCancelled || entry.orderId != null || entry.type === 'OPENING_BALANCE'

  const getEditTooltip = () => {
    if (entry.isCancelled) return t('tooltips.editDisabledCancelled')
    if (entry.orderId) return t('tooltips.editDisabledOrder')
    if (entry.type === 'OPENING_BALANCE') return t('tooltips.editDisabledOpeningBalance')
    return t('tooltips.edit')
  }

  const getCancelTooltip = () => {
    if (entry.isCancelled) return t('tooltips.cancelDisabledAlready')
    if (entry.orderId) return t('tooltips.cancelDisabledOrder')
    if (entry.type === 'OPENING_BALANCE') return t('tooltips.cancelDisabledOpeningBalance')
    return t('tooltips.cancel')
  }

  const getPrintTooltip = () => {
    if (entry.isCancelled) return t('tooltips.printDisabledCancelled')
    if (entry.orderId) return t('tooltips.printDisabledOrder')
    if (entry.type === 'OPENING_BALANCE') return t('tooltips.printDisabledOpeningBalance')
    return t('tooltips.printReceipt')
  }

  return (
    <div className="flex items-center gap-1">
      <Button
        plain
        disabled={isEditDisabled}
        onClick={() => onEditEntry && onEditEntry(entry)}
        aria-label={t('actions.edit')}
        className="p-1"
        title={getEditTooltip()}
      >
        <Pencil className={`h-4 w-4 ${
          isEditDisabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:text-blue-500 dark:hover:text-blue-400'
        }`}/>
      </Button>
      <Button
        plain
        disabled={isPrintDisabled}
        onClick={() => onPrintReceipt && onPrintReceipt(entry)}
        aria-label={t('actions.printReceipt')}
        className="p-1"
        title={getPrintTooltip()}
      >
        <Printer className={`h-4 w-4 ${
          isPrintDisabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:text-blue-500 dark:hover:text-blue-400'
        }`}/>
      </Button>
      <Button
        plain
        disabled={isCancelDisabled}
        onClick={() => onCancelEntry && onCancelEntry(entry)}
        aria-label={t('actions.cancel')}
        className="p-1"
        title={getCancelTooltip()}
      >
        <Trash2 className={`h-4 w-4 ${
          isCancelDisabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:text-red-500 dark:hover:text-red-400'
        }`}/>
      </Button>
      <div className="ml-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                <Info className="h-4 w-4 text-gray-500 dark:text-gray-400"/>
              </button>
            </TooltipTrigger>
            <TooltipContent side="left"
                            className="w-64 p-3 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 opacity-100">
              <TooltipContentComponent entry={entry} />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  )
}

export function createLedgerColumns(
  onEditEntry?: (entry: LedgerEntry) => void,
  onCancelEntry?: (entry: LedgerEntry) => void,
  onPrintReceipt?: (entry: LedgerEntry) => void
): ColumnDef<LedgerEntry>[] {
  return [
    {
      accessorKey: "voucherNumber",
      header: ({ column }) => sortableColumnHeader(column, 'fields.voucherNumber', 'ledger'),
      cell: ({ row }) => (
        <div className="text-right tabular-nums">
          <Text className={row.original.isCancelled ? 'line-through decoration-2' : ''}>
            {row.original.voucherNumber}
          </Text>
        </div>
      ),
      sortingFn: (rowA, rowB) => rowA.original.voucherNumber - rowB.original.voucherNumber,
    },
    {
      accessorKey: "transactionDate",
      header: ({ column }) => sortableColumnHeader(column, 'fields.transactionDate', 'ledger'),
      cell: ({ row }) => (
        <Text className={row.original.isCancelled ? 'line-through decoration-2' : ''}>
          {formatDateString(row.original.transactionDate)}
        </Text>
      ),
      sortingFn: (rowA, rowB) => new Date(rowA.original.transactionDate).getTime() - new Date(rowB.original.transactionDate).getTime(),
    },
    {
      accessorKey: "description",
      header: ({ column }) => sortableColumnHeader(column, 'fields.description', 'ledger'),
      cell: ({ row }) => <DescriptionCell entry={row.original} />,
    },
    {
      accessorKey: "type",
      header: () => <TypeHeader />,
      cell: ({ row }) => <TypeCell entry={row.original} />,
    },
    {
      accessorKey: "amount",
      header: ({ column }) => sortableColumnHeader(column, 'fields.amount', 'ledger'),
      cell: ({ row }) => (
        <div className="text-right tabular-nums">
          <Text
            className={`font-medium ${
              row.original.isCancelled ? 'line-through decoration-2' : ''
            } ${
              row.original.amount > 0
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400'
            }`}
          >
            {formatCurrency(row.original.amount)}
          </Text>
        </div>
      ),
      sortingFn: (rowA, rowB) => rowA.original.amount - rowB.original.amount,
    },
    {
      id: "actions",
      header: () => <ActionsHeader />,
      cell: ({ row }) => (
        <ActionsCell
          entry={row.original}
          onEditEntry={onEditEntry}
          onCancelEntry={onCancelEntry}
          onPrintReceipt={onPrintReceipt}
        />
      ),
    },
  ]
}
